/**
 * Auth + app-config context. Boots by fetching `/meta/config` (which returns the
 * enabled feature flags and, if a valid token is present, the current user), then
 * exposes login/logout and the runtime feature map to the whole app.
 *
 * When the backend has auth disabled, `/meta/config` returns an open superuser, so
 * downstream role checks and route guards behave uniformly.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { apiLogin, apiLogout } from "@/auth/api";
import { UNAUTHORIZED_EVENT, clearToken, setToken } from "@/lib/authToken";
import { clearSessionState } from "@/lib/useStoredState";
import { config as appConfig } from "@/config";
import {
  fetchConfig,
  type BrandAssets,
  type MetaConfig,
  type MetaUser,
} from "@/lib/meta";

interface AuthContextValue {
  loading: boolean;
  authEnabled: boolean;
  user: MetaUser | null;
  features: Record<string, boolean>;
  appName: string;
  appTagline: string;
  orgName: string;
  /** Logo paths for `<Brand>`; undefined until `/meta/config` resolves. */
  brand?: BrandAssets;
  isSynthetic: boolean;
  dataSourceBanner: string;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  hasRole: (...roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<MetaConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setConfig(await fetchConfig());
    } catch {
      // Backend unreachable: fall back to an empty, unauthenticated config so the
      // login screen can still render (auth assumed on when unknown).
      setConfig((prev) => prev ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A 401 anywhere drops the session; re-fetch config to reflect logged-out state.
  useEffect(() => {
    const onUnauthorized = () => {
      void refresh();
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiLogin(email, password);
      setToken(res.access_token);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      /* best-effort; clear locally regardless */
    }
    clearToken();
    // Session-scoped UI state belongs to the person who was signed in, not to
    // the tab. It outlives the sign-out unless it is dropped here, and the next
    // person to sign in on this machine would inherit it.
    clearSessionState();
    await refresh();
  }, [refresh]);

  const user = config?.user ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      authEnabled: config?.auth_enabled ?? true,
      user,
      features: config?.features ?? {},
      // Falls back to the build-time name rather than a second literal, so the
      // pre-config paint and the `<title>` baked into index.html agree.
      appName: config?.app_name ?? appConfig.appName,
      appTagline: config?.app_tagline ?? "",
      orgName: config?.org_name ?? "",
      brand: config?.brand,
      isSynthetic: config?.is_synthetic ?? true,
      dataSourceBanner: config?.data_source_banner ?? "SYNTHETIC DATA",
      login,
      logout,
      refresh,
      hasRole: (...roles: string[]) => {
        if (!user) return false;
        if (user.roles.includes("admin")) return true;
        return roles.some((r) => user.roles.includes(r));
      },
    }),
    [loading, config, user, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
